import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Briefcase, MessageSquare, Tag, MapPin, Palette } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  freelanceGigsApi,
  FreelanceGigResponseWithGig,
} from '../services/freelanceGigsApi';
import './FreelanceGigs.scss';

/**
 * Employee's "Mano Freelance atsakymai" page.
 * Lists every gig the employee has responded to with their current status
 * and a deep link into the message thread.
 */
const FreelanceMyResponses: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [responses, setResponses] = useState<FreelanceGigResponseWithGig[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { responses } = await freelanceGigsApi.myResponses();
      setResponses(responses);
    } catch (err) {
      console.error('Failed to load responses:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'employee') load();
  }, [isAuthenticated, user, load]);

  if (isLoading) return <div className="freelance-page-loading">Kraunama...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.user_type !== 'employee') return <Navigate to="/" replace />;

  return (
    <div className="freelance-gigs-page">
      <div className="freelance-page-header">
        <div className="freelance-page-title">
          <Briefcase size={22} />
          <h1>Mano Freelance atsakymai</h1>
        </div>
        <Link to="/freelance/browse" className="btn btn-secondary">
          <Palette size={16} /> Naršyti Gigus
        </Link>
      </div>

      {loading ? (
        <div className="freelance-page-loading">Kraunama...</div>
      ) : responses.length === 0 ? (
        <div className="freelance-empty-state">
          <div className="freelance-empty-icon"><Briefcase size={42} /></div>
          <h2>Dar nepateikėte nei vieno atsakymo</h2>
          <p>Apsilankykite Gigų sąraše ir pasirinkite Jus dominantį pasiūlymą.</p>
          <Link to="/freelance/browse" className="btn btn-primary btn-lg">
            <Palette size={16} /> Naršyti Gigus
          </Link>
        </div>
      ) : (
        <ul className="freelance-responses-list">
          {responses.map((r) => (
            <li key={r.id} className={`freelance-response-row freelance-response-row--${r.status}`}>
              <div className="freelance-response-main">
                <h3>
                  <Link to={`/freelance/browse/${r.gig.id}`}>{r.gig.title}</Link>
                </h3>
                <div className="freelance-response-meta">
                  {r.gig.work_fields.length > 0 && (
                    <span className="freelance-gig-tag"><Tag size={12} /> {r.gig.work_fields.join(', ')}</span>
                  )}
                  <span className="freelance-gig-tag"><MapPin size={12} />
                    {r.gig.nationwide ? 'Visoje Lietuvoje' : (r.gig.city || 'Nenurodyta')}
                  </span>
                  <span className={`freelance-response-status freelance-response-status--${r.status}`}>
                    {r.status === 'interested' ? 'Susidomėjau' : 'Atsisakiau'}
                  </span>
                </div>
              </div>
              <Link to={`/freelance/messages/${r.id}`} className="btn btn-secondary btn-sm freelance-thread-btn">
                <MessageSquare size={14} /> Žinutės
                {r.unread_count > 0 && (
                  <span className="freelance-unread-dot" aria-label="Neperskaitytos žinutės">
                    {r.unread_count}
                  </span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FreelanceMyResponses;
